import { useEffect } from 'react';
import { usePlanStore } from '../stores/planStore';
import { useAuthStore } from '../stores/authStore';
import { createClient } from '@supabase/supabase-js';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { useTranslation } from 'react-i18next';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

interface PlanGenerationParams {
  goal: string;
  calories_target: number;
  duration_days: number;
  meals_per_day: number;
  diet_type: string;
  allergies: string[];
  dislikes?: string[];
  activity_level: string;
  gender?: string;
  age?: number;
  current_weight?: number;
  target_weight?: number;
}

export function usePlan() {
  const store = usePlanStore();
  const { user } = useAuthStore();
  const { t } = useTranslation();

  // FIXED: Load plan data using the persistent store lock
  useEffect(() => {
    if (!user?.id || store.hasLoadedInitial) return;

    const loadData = async () => {
      // Load history
      await store.loadPlanHistory(user.id);

      // Load active plan if missing
      if (!store.activePlan || store.activeMeals.length === 0) {
        await store.loadActivePlan(user.id);
      }
    };

    loadData();
  }, [user?.id, store.hasLoadedInitial]);

  // Subscribe to realtime updates properly
  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = store.subscribeToPlanUpdates(user.id);

    return () => {
      unsubscribe();
    };
  }, [user?.id]);

  const generateNewPlan = async (params: PlanGenerationParams) => {
    if (!user?.id) {
      toast.error(t('toast.loginRequiredForPlan'));
      return;
    }

    try {
      store.setIsGenerating(true);
      store.setGenerationProgress(0);
      store.setError(null);

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) throw new Error('Authentication required');

      const response = await fetch(
        `${supabaseUrl}/functions/v1/generate-plan-full`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ user_id: user.id, ...params })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate plan');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      if (!reader) throw new Error('No response stream available');

      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const dataStr = line.slice(6).trim();
            if (!dataStr) continue;

            try {
              const data = JSON.parse(dataStr);
              if (data.progress !== undefined) store.setGenerationProgress(data.progress);
              if (data.complete) {
                toast.success(t('toast.planGeneratedSuccess'));
                await store.loadActivePlan(user.id);
                store.setIsGenerating(false);
                store.setGenerationProgress(100);
                return;
              }
              if (data.error) throw new Error(data.error);
            } catch (parseError) {
              console.warn('Failed to parse SSE data:', dataStr);
            }
          }
        }
      }

    } catch (error: any) {
      console.error('Plan generation failed:', error);
      store.setError(error.message);
      store.setIsGenerating(false);
      toast.error(t('toast.planGenerationFailed', { errorMessage: error.message }));
    }
  };

  const reloadPlan = async () => {
    if (!user?.id) return;
    await store.loadActivePlan(user.id);
  };

  const getCompletionStats = () => {
    const totalMeals = store.activeMeals.length;
    const completedMeals = store.activeMeals.filter(m => m.is_completed).length;
    const percentage = totalMeals > 0 ? Math.round((completedMeals / totalMeals) * 100) : 0;
    return { total: totalMeals, completed: completedMeals, percentage };
  };

  const getDailyTotals = (meals: any[]) => {
    const totals = { calories: 0, protein: 0, carbs: 0, fat: 0 };

    for (let i = 0; i < meals.length; i++) {
      const meal = meals[i];
      if (meal.is_completed) {
        totals.calories += meal.calories || 0;
        totals.protein += meal.protein || 0;
        totals.carbs += meal.carbs || 0;
        totals.fat += meal.fat || 0;
      }
    }

    return totals;
  };

  const getDailyTargets = (meals: any[]) => {
    return meals.reduce(
      (totals, meal) => ({
        calories: totals.calories + (meal.calories || 0),
        protein: totals.protein + (meal.protein || 0),
        carbs: totals.carbs + (meal.carbs || 0),
        fat: totals.fat + (meal.fat || 0)
      }), { calories: 0, protein: 0, carbs: 0, fat: 0 }
    );
  };

  const generateRecipeForMeal = async (mealId: string) => {
    if (!user?.id) { toast.error(t('toast.loginRequired')); return; }
    if (user.plan !== 'VIP') { toast.error(t('toast.recipeGenerationVipOnly')); return; }

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) throw new Error('Authentication required');

      const planId = store.activePlan?.id;

      const setBothMeals = (status: any, err?: string) => {
        store.setMeals(store.activeMeals.map(m => m.id === mealId ? { ...m, recipe_generation_status: status, recipe_generation_error: err || null } : m));
      };

      setBothMeals('generating');
      if (!session.access_token) {
        console.error("No session access token ", session.access_token);
        return;
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/resolve-single-recipe`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: planId, meal_id: mealId, user_id: user.id })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate recipe');
      }

      await store.loadActivePlan(user.id);
      toast.success(t('toast.recipeGeneratedSuccess'));

    } catch (error: any) {
      console.error('[usePlan] Recipe generation failed:', error);
      toast.error(t('toast.recipeGenerationFailed', { errorMessage: error.message }));
      throw error;
    }
  };

  return {
    ...store,
    activePlan: store.activePlan,
    planDays: store.activeDays,
    meals: store.activeMeals,

    generateNewPlan,
    reloadPlan,
    getCompletionStats,
    getDailyTotals,
    getDailyTargets,
    generateRecipeForMeal
  };
}